import {useState, useContext} from 'react';
import {Form} from 'react-bootstrap';
import {EmailSentNotice} from './EmailSentNotice';
import {LoginContext, LoginScreen} from './LoginContext';
import {PrimaryLogInButton, SignInButtonFlat} from './SignInButton';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {logProductionErrorNoHook} from 'components/errors/utils';
import {isBrowserOffline} from 'utils/connectionMessages';
import {
  emailDomainOf,
  emailSendAdvice,
  errorCodeOf,
  redactEmails,
  signInConnectionAdvice,
  type EmailSendAction,
} from './emailSendError';

/**
 * Asks for an email address and sends a one-time sign-in link or a password
 * reset to it. When Firebase refuses, it says why when it can, logs the error
 * code with the action, page origin and email domain (never the address), and
 * lets the student try again.
 *
 * `action` names which email this form sends, for the log. Omitted means
 * `'signInLink'`.
 */
export const EmailLinkform = ({
  auth,
  emailHandler,
  title,
  body,
  actionCodeSettings = {},
  action = 'signInLink',
}: {
  auth: unknown;
  emailHandler: (...args: unknown[]) => Promise<unknown>;
  title: string;
  body: string;
  actionCodeSettings?: object;
  action?: EmailSendAction;
}) => {
  const [, setLoginState] = useContext(LoginContext);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [error, setError] = useState('');
  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [loading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const t = useTranslateFunction('miscUI');

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setIsLoading(false);
    setSent(false);
    try {
      setIsLoading(true);
      await emailHandler(auth, magicLinkEmail, actionCodeSettings);
      setIsLoading(false);
      setSent(true);
    } catch (error) {
      setIsLoading(false);
      const code = errorCodeOf(error);
      const advice =
        signInConnectionAdvice({code, isOffline: isBrowserOffline()}) ??
        emailSendAdvice(code);
      setError(
        advice === undefined
          ? t('Error sending email ({{code}}).', {
              substitutions: {code: code ?? 'unknown'},
              forceString: true,
            })
          : t(advice, {forceString: true}),
      );
      const message = error instanceof Error ? error.message : String(error);
      void logProductionErrorNoHook({
        error: new Error(redactEmails(message)),
        additionalMetadata: {
          source: 'EmailLinkForm',
          action,
          code: code ?? null,
          origin: window.location.origin,
          emailDomain: emailDomainOf(magicLinkEmail),
        },
      }).catch(loggingError => {
        console.debug('[EmailLinkForm] could not log the send failure', loggingError);
      });
    }
  };

  return (
    <div className="d-flex flex-column align-items-center">
      {!sent ? (
        <>
          <h2>{t(title)}</h2>
          <p className="mt-2 mb-2 px-2 text-center">{t(body)}</p>
          <Form
            onSubmit={e => {
              handleSubmit(e);
            }}
          >
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Control
                style={{width: 300}}
                type="email"
                placeholder={t('Enter email', {forceString: true})}
                autoComplete="email"
                onChange={e => setMagicLinkEmail(e.target.value)}
                isInvalid={error.length > 0}
                aria-label={t('Enter email', {forceString: true})}
              />
              <Form.Control.Feedback type="invalid" role="alert">
                {error}
              </Form.Control.Feedback>
            </Form.Group>
            <PrimaryLogInButton
              variant="primary"
              type="submit"
              style={{width: '100%'}}
              className="mb-1"
              disabled={loading}
            >
              {loading ? t('Loading…') : t('Send Email')}
            </PrimaryLogInButton>
            <SignInButtonFlat
              variant="light"
              onClick={() => setLoginState(LoginScreen.INIT_LANDING)}
              style={{width: '100%'}}
            >
              {t('Go Back')}
            </SignInButtonFlat>
          </Form>
        </>
      ) : (
        <EmailSentNotice
          emailHandler={emailHandler}
          handlerArgs={[auth, magicLinkEmail, actionCodeSettings]}
        />
      )}
    </div>
  );
};
